import { parseLocationString } from "@/lib/locationUtils";
import {
  validateListingFreeTextField,
  validateListingTitleField,
} from "@/lib/listing/listingFreeTextPolicy";
import { getActiveEditListingImages } from "@/lib/listing/editListingImagesState";
import { isPriceAllowedUnderCeilingEnforcement } from "@/lib/pricing/sellerPriceBandCheck";
import { getSellerBandAboveRangeMessage } from "@/lib/pricingInsightCopy";
import { assertSellListingAbsolutePrice } from "@/lib/listing/sellListingContract";
import { validateOptionalCatalogBrand } from "@/lib/listing/listingIdentityPolicy";
import type { ItemCategory, ItemImage } from "@/lib/types";

export interface SellListingFormSlice {
  title: string;
  price: string;
  category: ItemCategory | "";
  location: string;
  brand?: string;
}

export interface SellListingFormErrors {
  title?: string;
  description?: string;
  defects?: string;
  price?: string;
  category?: string;
  location?: string;
  pickupDays?: string;
  images?: string;
  brand?: string;
}

export function validateSellListingField(
  name: string,
  value: unknown,
  context?: { category?: ItemCategory | string | null },
): string | undefined {
  switch (name) {
    case "title": {
      const v = String(value ?? "");
      if (!v.trim()) return "Title is required";
      if (v.length < 5) return "Title must be at least 5 characters";
      return validateListingTitleField(v);
    }
    case "description":
    case "defects":
      return validateListingFreeTextField(String(value ?? ""));
    case "price": {
      const check = assertSellListingAbsolutePrice(String(value ?? ""));
      return check.ok ? undefined : check.message;
    }
    case "category":
      if (!value) return "Category is required";
      return undefined;
    case "brand":
      return validateOptionalCatalogBrand(
        String(value ?? ""),
        context?.category,
      );
    case "location": {
      const v = String(value ?? "");
      if (!v) return "Location is required";
      const parsed = parseLocationString(v);
      if (!parsed.lga) {
        return "Please select a specific area (LGA) in addition to the state";
      }
      return undefined;
    }
    case "pickupDays":
      return undefined;
    case "images": {
      const imgs = getActiveEditListingImages((value as ItemImage[]) || []);
      if (!imgs || imgs.length !== 3) return "Exactly 3 images are required";
      if (!imgs.some((img: ItemImage) => img.isMain))
        return "Select one image as the main image";
      if (imgs.some((img: ItemImage) => !img.file)) {
        return "One or more images are invalid. Please try uploading them again.";
      }
      return undefined;
    }
    default:
      return undefined;
  }
}

export function getSellListingFormErrors(
  slice: SellListingFormSlice,
  uploadedImages: ItemImage[],
): SellListingFormErrors {
  return {
    title: validateSellListingField("title", slice.title),
    price: validateSellListingField("price", slice.price),
    category: validateSellListingField("category", slice.category),
    location: validateSellListingField("location", slice.location),
    brand: validateSellListingField("brand", slice.brand ?? "", {
      category: slice.category,
    }),
    images: validateSellListingField("images", uploadedImages),
  };
}

export function sellListingFormIsValid(errors: SellListingFormErrors): boolean {
  return !Object.values(errors).some((error) => error !== undefined);
}

/** Validates listing fields that belong to each item (excludes location — common in bulk mode). */
export function getSellListingPerItemFieldErrors(
  slice: Pick<SellListingFormSlice, "title" | "price" | "category" | "brand">,
  uploadedImages: ItemImage[],
): Pick<
  SellListingFormErrors,
  "title" | "price" | "category" | "images" | "brand"
> {
  return {
    title: validateSellListingField("title", slice.title),
    price: validateSellListingField("price", slice.price),
    category: validateSellListingField("category", slice.category),
    brand: validateSellListingField("brand", slice.brand ?? "", {
      category: slice.category,
    }),
    images: validateSellListingField("images", uploadedImages),
  };
}

export type SellListingPriceBand = {
  min: number;
  max: number;
  enforcementEligible?: boolean;
};

/** Client mirror of server band check when band is known (Phase 3). */
export function validatePriceAgainstBand(
  price: string,
  band: SellListingPriceBand | null | undefined,
  options?: { enforcementActive?: boolean },
): string | undefined {
  if (!options?.enforcementActive || !band?.enforcementEligible) {
    return undefined;
  }
  const numericPrice = Number(price.replace(/,/g, ""));
  if (!price || Number.isNaN(numericPrice)) {
    return undefined;
  }
  if (!isPriceAllowedUnderCeilingEnforcement(numericPrice, band)) {
    return getSellerBandAboveRangeMessage();
  }
  return undefined;
}

export function sellListingPerItemFieldsValid(
  errors: Pick<
    SellListingFormErrors,
    | "title"
    | "price"
    | "category"
    | "images"
    | "description"
    | "defects"
    | "brand"
  >,
): boolean {
  return !Object.values(errors).some((error) => error !== undefined);
}

/** Throws before any item row is inserted when photos are not ready to upload. */
export function assertSellListingImagesReady(
  uploadedImages: ItemImage[],
): void {
  const error = validateSellListingField("images", uploadedImages);
  if (error) {
    throw new Error(error);
  }
}
